export {};

// app.luwu.dev/account: sign in with your Luwu account and see what your
// account used (relay traffic to and from your computers, sessions,
// computers), for billing.

import { onLuwu } from "./server";

const ACCOUNTS = "https://accounts.luwu.dev";
const LUWU = "https://api.luwu.dev";
const TOKEN = "luwu-token";

const $ = <T extends HTMLElement>(id: string) => document.getElementById(id) as T;
const get = (k: string) => {
  try {
    return localStorage.getItem(k);
  } catch {
    return null;
  }
};
const set = (k: string, v: string | null) => {
  try {
    if (v == null) localStorage.removeItem(k);
    else localStorage.setItem(k, v);
  } catch {
    // private mode: signed in for this visit only
  }
};

let token: string | null = get(TOKEN);
let email = "";
let days = 30;

function view(v: "email" | "code" | "usage" | "loading") {
  for (const id of ["email", "code", "usage", "loading"]) $(`v-${id}`).hidden = id !== v;
  $("nav").hidden = v !== "usage" && v !== "loading";
  if (v === "usage") void showWho();
}

/** Name or email next to the menu (Luwu's /v1/me). */
async function showWho() {
  try {
    const r = await fetch(`${LUWU}/v1/me`, { headers: { authorization: `Bearer ${token}` } });
    if (!r.ok) return;
    const me = (await r.json()) as { display_name: string | null; username: string | null; email: string | null };
    $("who").textContent = me.display_name || me.username || me.email || "";
  } catch {
    // just no name
  }
}

async function post<T>(url: string, body: unknown): Promise<T> {
  let r: Response;
  try {
    r = await fetch(url, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(body) });
  } catch {
    throw new Error("Couldn't reach the sign-in server. Check your connection.");
  }
  const data = (await r.json().catch(() => ({}))) as T & { error?: string; message?: string };
  if (!r.ok) throw new Error(r.status === 429 ? "Too many attempts. Wait a minute and try again." : (data.message ?? data.error ?? `Sign-in failed (${r.status})`));
  return data;
}

$<HTMLFormElement>("f-email").addEventListener("submit", async (e) => {
  e.preventDefault();
  const err = $("e-email");
  err.textContent = "";
  email = $<HTMLInputElement>("email").value.trim();
  if (!email.includes("@")) return void (err.textContent = "Enter your email address.");
  try {
    await post(`${ACCOUNTS}/v1/auth/email/start`, { email });
    $("sent-to").textContent = email;
    view("code");
    $<HTMLInputElement>("code").focus();
  } catch (x) {
    err.textContent = (x as Error).message;
  }
});

$<HTMLFormElement>("f-code").addEventListener("submit", async (e) => {
  e.preventDefault();
  const err = $("e-code");
  err.textContent = "";
  const code = $<HTMLInputElement>("code").value.trim();
  try {
    const r = await post<{ token?: string; signup?: unknown }>(`${ACCOUNTS}/v1/auth/email/verify`, { email, code, deviceName: "Luwu web" });
    if (!r.token) {
      err.textContent = "There's no account for this email yet. Create one in the Luwu app or at app.luwu.dev, then sign in here.";
      return;
    }
    token = r.token;
    set(TOKEN, token);
    void load();
  } catch (x) {
    err.textContent = (x as Error).message;
  }
});

$("back").addEventListener("click", () => view("email"));
$("sign-out").addEventListener("click", () => {
  token = null;
  set(TOKEN, null);
  view("email");
});
for (const b of document.querySelectorAll<HTMLButtonElement>("[data-days]")) {
  b.addEventListener("click", () => {
    days = Number(b.dataset.days);
    void load();
  });
}

interface Totals {
  relay_in: number;
  relay_out: number;
  sessions: number;
  guest_sessions: number;
  session_seconds: number;
  online_seconds: number;
}
interface Report {
  from: string;
  to: string;
  days: (Totals & { day: string; computers: number })[];
  computers: (Totals & { id: string; name: string; platform: string; removed: boolean })[];
  totals: Totals & { computer_days: number };
  now: { computers: number; online: number; active_shares: number };
}

const size = (n: number) =>
  n < 1024 ? `${n} B` : n < 1 << 20 ? `${(n / 1024).toFixed(1)} KB` : n < 1 << 30 ? `${(n / (1 << 20)).toFixed(1)} MB` : `${(n / (1 << 30)).toFixed(2)} GB`;
const hours = (s: number) => (s < 3600 ? `${Math.round(s / 60)} min` : `${(s / 3600).toFixed(1)} h`);

function cell(tag: string, text: string, cls?: string) {
  const el = document.createElement(tag);
  el.textContent = text;
  if (cls) el.className = cls;
  return el;
}

async function load() {
  if (!token) return view("email");
  view("loading");
  let r: Response;
  try {
    r = await fetch(`${LUWU}/v1/usage?days=${days}`, { headers: { authorization: `Bearer ${token}` } });
  } catch {
    $("loading-msg").textContent = "Couldn't reach Luwu. Check your connection and reload.";
    return;
  }
  if (r.status === 401) {
    token = null;
    set(TOKEN, null);
    return view("email");
  }
  const u = (await r.json()) as Report;
  for (const b of document.querySelectorAll<HTMLButtonElement>("[data-days]")) b.setAttribute("aria-pressed", String(Number(b.dataset.days) === days));
  $("range").textContent = `${u.from} – ${u.to} (UTC)`;
  $("k-out").textContent = size(u.totals.relay_out);
  $("k-in").textContent = size(u.totals.relay_in);
  $("k-sessions").textContent = String(u.totals.sessions);
  $("k-sessions-sub").textContent = `${hours(u.totals.session_seconds)} · ${u.totals.guest_sessions} by friends`;
  $("k-computers").textContent = String(u.now.computers);
  $("k-computers-sub").textContent = `${u.now.online} online · ${u.totals.computer_days} computer-days`;

  // Daily bars: to computers (gold) stacked on from computers (slate).
  const chart = $("chart");
  chart.replaceChildren();
  const byDay = new Map(u.days.map((d) => [d.day, d]));
  const all: string[] = [];
  for (let t = Date.parse(`${u.from}T00:00:00Z`); t <= Date.parse(`${u.to}T00:00:00Z`); t += 86400_000) all.push(new Date(t).toISOString().slice(0, 10));
  const max = Math.max(1, ...u.days.map((d) => d.relay_in + d.relay_out));
  for (const day of all) {
    const d = byDay.get(day);
    const col = document.createElement("div");
    col.className = "col";
    col.title = d ? `${day}: ${size(d.relay_out)} to computers, ${size(d.relay_in)} from computers, ${d.sessions} sessions` : `${day}: no use`;
    const out = document.createElement("i");
    out.className = "out";
    out.style.height = `${(100 * (d?.relay_out ?? 0)) / max}%`;
    const inn = document.createElement("i");
    inn.className = "in";
    inn.style.height = `${(100 * (d?.relay_in ?? 0)) / max}%`;
    col.append(out, inn);
    chart.append(col);
  }
  $("chart-max").textContent = size(max);
  $("chart-first").textContent = all[0] ?? "";
  $("chart-last").textContent = all.at(-1) ?? "";

  const body = $("rows");
  body.replaceChildren();
  for (const k of u.computers) {
    const tr = document.createElement("tr");
    tr.append(
      cell("td", k.name + (k.removed ? " (removed)" : "")),
      cell("td", size(k.relay_out), "num"),
      cell("td", size(k.relay_in), "num"),
      cell("td", String(k.sessions), "num"),
      cell("td", hours(k.session_seconds), "num"),
      cell("td", hours(k.online_seconds), "num"),
    );
    body.append(tr);
  }
  if (!u.computers.length) {
    const tr = document.createElement("tr");
    const td = cell("td", "No computers yet. Run curl -fsSL https://luwu.dev/install.sh | sh on a computer, then luwu, to add one.") as HTMLTableCellElement;
    td.colSpan = 6;
    tr.append(td);
    body.append(tr);
  }
  view("usage");
  void loadTokens();
  let n: string | null = null;
  try {
    n = sessionStorage.getItem("luwu-next");
    sessionStorage.removeItem("luwu-next");
  } catch {
    // no storage: stay
  }
  if (n) location.href = n;
}

// Google, through the account service: it sends a one-time code back here (?code=), which
// only this browser can redeem, with the PKCE verifier kept for the trip.
const VERIFIER = "luwu-google-verifier";
const b64url = (b: Uint8Array) => btoa(String.fromCharCode(...b)).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
$("google").addEventListener("click", async () => {
  const verifier = b64url(crypto.getRandomValues(new Uint8Array(48)));
  const challenge = b64url(new Uint8Array(await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier))));
  try {
    sessionStorage.setItem(VERIFIER, verifier);
  } catch {
    $("e-email").textContent = "This browser blocks storage, so Google sign-in can't finish here. Use the email code.";
    return;
  }
  location.href = `${ACCOUNTS}/v1/auth/google/start?${new URLSearchParams({ client: "mobile", redirect: `${location.origin}/account`, code_challenge: challenge })}`;
});

async function finishGoogle(code: string) {
  let verifier: string | null = null;
  try {
    verifier = sessionStorage.getItem(VERIFIER);
    sessionStorage.removeItem(VERIFIER);
  } catch {
    // fall through
  }
  history.replaceState(null, "", location.pathname);
  view("email");
  if (!verifier) return void ($("e-email").textContent = "Google sign-in expired. Try again.");
  try {
    const r = await post<{ token?: string; signup?: unknown }>(`${ACCOUNTS}/v1/auth/token`, { code, codeVerifier: verifier, deviceName: "Luwu web" });
    if (!r.token) {
      $("e-email").textContent = "There's no account for this Google email yet. Create one in the Luwu app or at app.luwu.dev, then sign in here.";
      return;
    }
    token = r.token;
    set(TOKEN, token);
    void load();
  } catch (x) {
    $("e-email").textContent = (x as Error).message;
  }
}

const q = new URLSearchParams(location.search);
// /account?next=/computers: go back there once signed in (kept across the Google trip).
const next = q.get("next");
if (next?.startsWith("/") && !next.startsWith("//")) {
  try {
    sessionStorage.setItem("luwu-next", next);
  } catch {
    // stays on this page
  }
}
if (q.get("code")) void finishGoogle(q.get("code")!);
else if (q.get("error")) {
  history.replaceState(null, "", location.pathname);
  view("email");
  $("e-email").textContent = q.get("error") === "access_denied" ? "Google sign-in was cancelled." : `Google sign-in failed (${q.get("error")}).`;
} else if (token && onLuwu()) void load();
else location.replace("/computers"); // sign in on the console (a company server has no usage page here) // sign in on the console, which links back here

// Access tokens (POST /v1/notify from scripts and CI): list, make, revoke.

interface AccessToken {
  id: string;
  name: string;
  hint: string;
  token?: string;
  expires_at: number | null;
  last_used_at: number | null;
}

async function api<T>(method: string, path: string, body?: unknown): Promise<T> {
  const r = await fetch(`${LUWU}${path}`, {
    method,
    headers: { authorization: `Bearer ${token}`, ...(body ? { "content-type": "application/json" } : {}) },
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = (await r.json().catch(() => ({}))) as T & { error?: string };
  if (!r.ok) throw new Error(data.error ?? `Failed (${r.status})`);
  return data;
}

const day = (s: number | null) => (s ? new Date(s * 1000).toLocaleDateString() : "");

async function loadTokens() {
  const body = $("token-rows");
  try {
    const { tokens } = await api<{ tokens: AccessToken[] }>("GET", "/v1/tokens");
    body.replaceChildren();
    if (tokens.length === 0) {
      const tr = document.createElement("tr");
      const td = cell("td", "No tokens yet.") as HTMLTableCellElement;
      td.colSpan = 5;
      tr.append(td);
      body.append(tr);
    }
    for (const t of tokens) {
      const tr = document.createElement("tr");
      const revoke = document.createElement("button");
      revoke.className = "linkbtn";
      revoke.type = "button";
      revoke.textContent = "Revoke";
      revoke.addEventListener("click", async () => {
        if (!confirm(`Revoke “${t.name}”? Scripts using it can't send notifications any more.`)) return;
        try {
          await api("DELETE", `/v1/tokens/${t.id}`);
          void loadTokens();
        } catch (x) {
          $("e-token").textContent = (x as Error).message;
        }
      });
      const actions = document.createElement("td");
      actions.append(revoke);
      tr.append(cell("td", t.name), cell("td", `…${t.hint}`), cell("td", day(t.last_used_at) || "never"), cell("td", day(t.expires_at) || "never"), actions);
      body.append(tr);
    }
  } catch (x) {
    $("e-token").textContent = (x as Error).message;
  }
}

$<HTMLFormElement>("f-token").addEventListener("submit", async (e) => {
  e.preventDefault();
  const err = $("e-token");
  err.textContent = "";
  const name = $<HTMLInputElement>("token-name").value.trim();
  if (!name) return void (err.textContent = "Name the token, so you know where it's used.");
  try {
    const t = await api<AccessToken>("POST", "/v1/tokens", { name });
    $("token-value").textContent = t.token ?? "";
    $("token-made").hidden = false;
    $<HTMLInputElement>("token-name").value = "";
    void loadTokens();
  } catch (x) {
    err.textContent = (x as Error).message;
  }
});
$("token-copy").addEventListener("click", () => void navigator.clipboard.writeText($("token-value").textContent ?? ""));
